import OpenInNew from "@mui/icons-material/OpenInNew";
import { Button, Tooltip } from "@mui/material";
import { AutomationRunner } from "encampy";
import { useCallback, useMemo, useState } from "react";

import { runMultiPhaseAutomation } from "../../util/encampy/runMultiPhaseAutomation";

export interface EncampyLaunchData {
  url: string;
  automation: (runner: AutomationRunner) => Promise<void>;
}

export interface EncampyAutomationLauncherProps {
  launchData: EncampyLaunchData;
  buttonText?: string;
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  disabledReason?: string;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  onBeforeLaunch?: () => Promise<EncampyLaunchData | null>;
}

export function EncampyAutomationLauncher({
  launchData,
  buttonText = "Open with Encampy",
  size = "small",
  disabled = false,
  disabledReason,
  onSuccess,
  onError,
  onBeforeLaunch,
}: EncampyAutomationLauncherProps) {
  const { url, automation } = launchData;

  const [launching, setLaunching] = useState(false);

  const computedDisabledReason = useMemo((): string | null => {
    // If a custom disabled reason is provided, use it when disabled
    if (disabled && disabledReason) {
      return disabledReason;
    }

    if (!url || url.trim() === "") {
      return "URL is required";
    }

    // Must have automation function
    if (!automation) {
      return "Automation function is required";
    }

    // Validate URL format
    try {
      new URL(url);
    } catch {
      return "Invalid URL format";
    }

    if (disabled) {
      return "Automation is disabled";
    }

    return null;
  }, [url, automation, disabled, disabledReason]);

  const isDisabled = launching || computedDisabledReason !== null;

  const handleClick = useCallback(async () => {
    if (computedDisabledReason) {
      onError?.(computedDisabledReason);
      return;
    }

    setLaunching(true);
    try {
      let effectiveUrl = url;
      let effectiveAutomation = automation;

      if (onBeforeLaunch) {
        const result = await onBeforeLaunch();
        if (!result) {
          // Lock failed or was aborted; caller already handled the error
          return;
        }
        effectiveUrl = result.url;
        effectiveAutomation = result.automation;
      }

      await runMultiPhaseAutomation(effectiveUrl, effectiveAutomation);
      onSuccess?.("Automation completed successfully");
    } catch (error) {
      onError?.(
        error instanceof Error ? error.message : "Failed to run automation - Unknown error"
      );
    } finally {
      setLaunching(false);
    }
  }, [url, automation, computedDisabledReason, onSuccess, onError, onBeforeLaunch]);

  const displayButtonText = launching ? "Running..." : buttonText;

  const button = (
    <Button
      disabled={isDisabled}
      onClick={handleClick}
      size={size}
      startIcon={<OpenInNew sx={{ fontSize: 18 }} />}
    >
      {displayButtonText}
    </Button>
  );

  if (computedDisabledReason && !launching) {
    return (
      <Tooltip title={computedDisabledReason} placement="top">
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
}
